import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { collection, doc, Firestore } from 'firebase/firestore';
import { docData } from '@pedix-workspace/angular-firebase';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import {
  EstablishmentCheckout,
  PurchaseOptions,
  toCheckoutShippingType,
  toPurchaseOptions,
} from '@pedix-workspace/utils';
import { getEstablishmentCheckoutConverter } from '@pedix-workspace/shared-models';
import { AppRequestContextService } from '../../app-request-context.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'apps/pedixapp/src/environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { isPlatformServer } from '@angular/common';
import { FIREBASE_FIRESTORE } from '@pedix-workspace/angular-firebase';

@Injectable({
  providedIn: 'root',
})
export class EstablishmentsCheckoutService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private appRequestContextService = inject(AppRequestContextService);
  private http = inject(HttpClient);
  private deviceService = inject(DeviceDetectorService);
  private platformId = inject(PLATFORM_ID);
  private transloco = inject(TranslocoService);
  private activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  readonly checkoutDataCache = signal<Record<string, EstablishmentCheckout | null>>({});
  private readonly pendingRequests = new Map<string, Promise<EstablishmentCheckout | null>>();

  readonly purchaseOptions = computed<PurchaseOptions | null>(() => {
    const establishment = this.appRequestContextService.establishment();

    if (!establishment?.features?.includes('PURCHASE_RESTRICTIONS')) {
      return null;
    }
    const checkout = this.checkoutDataCache()[establishment.id];

    if (!checkout) {
      return null;
    }
    const integrations = (this.appRequestContextService.availableIntegrations() || []).map(
      integration => ({ ...integration, active: true }),
    );
    const purchaseOptions = toPurchaseOptions(checkout, integrations);
    const activeLang = this.activeLang();

    return {
      ...purchaseOptions,
      shippingOptions: purchaseOptions.shippingOptions.map(option => ({
        ...option,
        name: this.transloco.translate(
          `productRestriction.shippingOption_${toCheckoutShippingType(option.type)}`,
          {},
          activeLang,
        ),
      })),
    };
  });

  get collectionRef() {
    return collection(this.firestore, 'establishmentsCheckout').withConverter(
      getEstablishmentCheckoutConverter({
        features: this.appRequestContextService.establishment().features,
      }),
    );
  }

  get isInstagram() {
    return this.deviceService.userAgent().includes('Instagram');
  }

  setCheckoutDataCache(establishmentId: string, checkoutData: EstablishmentCheckout | null) {
    this.checkoutDataCache.update(checkoutDataCache => ({
      ...checkoutDataCache,
      [establishmentId]: checkoutData,
    }));
  }

  get(establishmentId: string): Promise<EstablishmentCheckout | null> {
    if (isPlatformServer(this.platformId)) {
      return Promise.resolve(null);
    }
    const cached = this.checkoutDataCache()[establishmentId];

    if (cached) {
      return Promise.resolve(cached);
    }
    return this.refresh(establishmentId);
  }

  refresh(establishmentId: string): Promise<EstablishmentCheckout | null> {
    if (isPlatformServer(this.platformId)) {
      return Promise.resolve(null);
    }
    const pending = this.pendingRequests.get(establishmentId);

    if (pending) {
      return pending;
    }
    const request = this.fetch(establishmentId)
      .then(checkoutData => {
        this.setCheckoutDataCache(establishmentId, checkoutData);

        return checkoutData;
      })
      .finally(() => this.pendingRequests.delete(establishmentId));

    this.pendingRequests.set(establishmentId, request);

    return request;
  }

  private async fetch(establishmentId: string): Promise<EstablishmentCheckout | null> {
    if (this.isInstagram) {
      // NOTE: we needed to do this because in Instagram in-app browser we could not get the firestore query to resolve
      const apiUrl = `${environment.apiV1Url}/establishments/${establishmentId}/checkout`;

      const { checkoutData } = await firstValueFrom(
        this.http.get<{ checkoutData: EstablishmentCheckout }>(apiUrl),
      ).catch(error => {
        console.error('Error fetching checkout data:', error);

        return { checkoutData: null };
      });

      return checkoutData;
    }
    return firstValueFrom(docData(doc(this.collectionRef, establishmentId)));
  }
}
